import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CheckCircle, ArrowLeft, Loader2, Lock, Upload, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const DOCUMENT_LIST = [
  { key: 'offerLetterSigned',     label: 'Offer Letter (Signed)',                 format: 'PDF', required: true },
  { key: 'annexure1Signed',       label: 'Annexure 1 (Signed)',                   format: 'PDF', required: true },
  { key: 'annexure2Signed',       label: 'Annexure 2 (Signed)',                   format: 'PDF', required: true },
  { key: 'declarationNotarized',  label: 'Declaration (Notarized)',               format: 'PDF', required: true },
  { key: 'policeVerification',    label: 'Police Verification Certificate',       format: 'PDF', required: true },
  { key: 'medicalCertificate',    label: 'Medical Fitness Certificate',           format: 'PDF', required: true },
  { key: 'panCard',               label: 'PAN Card',                              format: 'PDF', required: true },
  { key: 'aadhaarCard',           label: 'Aadhaar Card',                          format: 'PDF', required: true },
  { key: 'voterIdCard',           label: 'Voter ID Card',                         format: 'PDF', required: true },
  { key: 'bankPassbook',          label: 'Bank Passbook (Front Page)',            format: 'PDF', required: true },
  { key: 'passportPhoto',         label: 'Passport Size Photo (Blue Background)', format: 'JPG', required: true },
  { key: 'eshramCard',            label: 'e-Shram Card',                          format: 'PDF', required: false, note: 'Mandatory for Labour & Ancillary staff' },
  { key: 'higherQualification',   label: 'Higher Qualification Certificate',      format: 'PDF', required: true },
  { key: 'additionalQualification', label: 'Additional Qualification (if any)',   format: 'PDF', required: false, note: 'Optional' },
];

const DocUploadSlot = ({ doc, uploaded, formNo, workerName, designation, onUploadSuccess, onDeleteDoc }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 200 * 1024;
    if (file.size > maxSize) {
      setError(`File size is ${(file.size / 1024).toFixed(0)}KB. Max limit is 200KB.`);
      return;
    }

    setError('');
    setUploading(true);
    const formData = new FormData();
    formData.append(doc.key, file);
    if (workerName) formData.append('workerName', workerName);
    if (designation) formData.append('designation', designation);

    try {
      const res = await axios.post(
        `${API_BASE}/api/worker-portal/documents/${encodeURIComponent(formNo)}/upload/${doc.key}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        onUploadSuccess(doc.key, res.data.url);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const isUploaded = !!uploaded;

  return (
    <div className={`p-4 rounded-2xl border-2 transition-all duration-200 ${isUploaded ? 'border-green-300 bg-green-50/60' : 'border-slate-200 bg-white hover:border-indigo-300 shadow-sm'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 mb-1">
            {isUploaded
              ? <CheckCircle size={18} className="text-green-600 shrink-0" />
              : <div className={`w-4 h-4 rounded-full border-2 shrink-0 ${doc.required ? 'border-red-500' : 'border-slate-400'}`} />
            }
            <p className={`text-xs md:text-sm font-black uppercase truncate ${isUploaded ? 'text-green-800' : 'text-slate-800'}`}>
              {doc.label}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 ml-6">
            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${doc.format === 'JPG' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
              {doc.format}
            </span>
            <span className="text-[10px] text-slate-500 font-bold">Max 200KB</span>
            {doc.note && <span className="text-[10px] text-orange-600 font-bold">{doc.note}</span>}
          </div>
          {isUploaded && (
            <p className="text-[10px] text-green-700 font-bold ml-6 mt-1">
              ✓ Uploaded {new Date(uploaded.uploadedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </p>
          )}
          {error && <p className="text-xs text-red-600 font-bold ml-6 mt-1">⚠️ {error}</p>}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {uploading ? (
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Loader2 size={18} className="animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              <button
                onClick={() => inputRef.current?.click()}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all active:scale-95 shadow-sm ${isUploaded ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                title={isUploaded ? 'Re-upload Document' : 'Upload Document'}
              >
                {isUploaded ? <CheckCircle size={18} /> : <Upload size={18} />}
              </button>
              {isUploaded && onDeleteDoc && (
                <button
                  onClick={() => onDeleteDoc(doc.key)}
                  className="w-10 h-10 rounded-xl bg-red-100 hover:bg-red-200 text-red-700 flex items-center justify-center transition-all active:scale-95 shadow-sm"
                  title="Delete Document"
                >
                  🗑️
                </button>
              )}
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={doc.format === 'JPG' ? '.jpg,.jpeg,image/jpeg' : '.pdf,application/pdf'}
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>
    </div>
  );
};

const WorkerStatus = () => {
  const [portalConfig, setPortalConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  const [formData, setFormData] = useState({ formNo: '', name: '', mobileNo: '', panNo: '', aadhaarLast6: '' });
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [uploadedDocs, setUploadedDocs] = useState({});
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [tncAgreed, setTncAgreed] = useState(false);
  const [submittingFinal, setSubmittingFinal] = useState(false);
  const [isFinalSubmitted, setIsFinalSubmitted] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/worker-portal/config`)
      .then(r => r.json())
      .then(data => setPortalConfig(data))
      .catch(() => setPortalConfig({ active: false, mode: 'open' }))
      .finally(() => setConfigLoading(false));
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setWorker(null);
    try {
      const res = await axios.post(`${API_BASE}/api/worker-portal/verify`, {
        formNo: formData.formNo.trim().toUpperCase(),
        name: formData.name.trim(),
        mobileNo: formData.mobileNo.trim(),
        panNo: formData.panNo.trim().toUpperCase(),
        aadhaarLast6: formData.aadhaarLast6.trim()
      }, { timeout: 20000 });

      if (res.data.success) {
        setWorker(res.data);
        setUploadedDocs(res.data.uploadedDocs || {});
        setIsFinalSubmitted(!!res.data.finalSubmitted);
      } else {
        setError(res.data.message || 'Verification failed.');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Portal is currently inactive. Please contact SGCL office.');
      } else if (err.response?.status === 404) {
        setError(err.response.data.message || 'Record not found.');
      } else if (err.response?.status === 401) {
        setError(err.response.data.message || 'Details do not match. Please check and retry.');
      } else if (err.response?.status === 503) {
        setError('Verification service temporarily unavailable. Please try again in a few minutes.');
      } else {
        setError('Connection error. Please check your internet and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDocUploadSuccess = (docKey, url) => {
    setUploadedDocs(prev => ({
      ...prev,
      [docKey]: { url, uploadedAt: new Date().toISOString() }
    }));
  };

  const handleDeleteDoc = async (docKey) => {
    if (!window.confirm('Delete this uploaded document?')) return;
    try {
      const res = await axios.delete(`${API_BASE}/api/worker-portal/documents/${encodeURIComponent(worker.formNo)}/doc/${docKey}`);
      if (res.data.success) {
        setUploadedDocs(prev => {
          const next = { ...prev };
          delete next[docKey];
          return next;
        });
      }
    } catch (err) {
      alert('Failed to delete document.');
    }
  };

  const handleFinalSubmit = async () => {
    if (!tncAgreed) return;
    setSubmittingFinal(true);
    try {
      const res = await axios.post(`${API_BASE}/api/worker-portal/documents/${encodeURIComponent(worker.formNo)}/final-submit`, {
        tncAgreed: true
      });
      if (res.data.success) {
        setIsFinalSubmitted(true);
        setShowReviewModal(false);
        setWorker(prev => ({
          ...prev,
          adminStatus: res.data.adminStatus,
          adminStatusMessage: res.data.adminStatusMessage
        }));
        alert('🎉 Application & Documents Successfully Submitted to SGCL Admin!');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit application.');
    } finally {
      setSubmittingFinal(false);
    }
  };

  const uploadedCount = Object.keys(uploadedDocs).length;
  const totalRequired = DOCUMENT_LIST.filter(d => d.required).length;
  const requiredUploaded = DOCUMENT_LIST.filter(d => d.required && uploadedDocs[d.key]).length;

  // Loading state
  if (configLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  // Portal inactive screen
  if (!portalConfig?.active) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-slate-800 rounded-[2.5rem] p-10 border border-slate-700 shadow-2xl">
            <div className="w-20 h-20 rounded-full bg-slate-700 flex items-center justify-center mx-auto mb-6 border-4 border-slate-600">
              <Lock size={36} className="text-slate-400" />
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-950 border border-red-800 mb-6">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span className="text-[9px] font-black text-red-400 uppercase tracking-[0.3em]">Portal Offline</span>
            </div>
            <h1 className="text-2xl font-black text-white uppercase italic tracking-tighter leading-tight mb-3">
              Portal Inactive
            </h1>
            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-2">
              Authorised Login Only
            </p>
            <div className="mt-6 p-4 bg-slate-900 rounded-2xl border border-slate-700">
              <p className="text-slate-500 text-[10px] font-semibold leading-relaxed">
                The Worker Verification Hub is currently offline. This portal is accessible only during active registration drives. Please contact the SGCL NFR Zone office for assistance.
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="mt-8 flex items-center gap-2 mx-auto bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95"
            >
              <ArrowLeft size={14} /> Back to Portal
            </button>
          </div>
          <p className="mt-6 text-slate-600 text-[9px] font-bold uppercase tracking-[0.4em]">Shramjivi Goodshed Corporation Limited · NFR Zone</p>
        </div>
      </div>
    );
  }

  // Verified worker — Dashboard with Left Content & Right Communication Panel
  if (worker) {
    return (
      <div className="min-h-screen bg-slate-100 font-sans text-slate-900 pb-12">

        {/* ── TOP HEADER BAR ── */}
        <div className="bg-slate-900 border-b-4 border-orange-500 shadow-xl">
          <div className="max-w-[1700px] mx-auto px-6 py-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-orange-500/20 border border-orange-500/40 flex items-center justify-center shrink-0">
                <span className="text-2xl">👷</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-xs font-black text-green-400 uppercase tracking-widest">Identity Verified & Active</span>
                </div>
                <h1 className="text-xl lg:text-2xl font-black text-white uppercase italic tracking-tight">{worker.workerName}</h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <div className="bg-slate-800 px-4 py-2 rounded-xl border border-slate-700">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ACK Number</p>
                <p className="text-sm font-mono font-black text-orange-400">{worker.formNo}</p>
              </div>
              <div className="bg-slate-800 px-4 py-2 rounded-xl border border-slate-700">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Date of Birth</p>
                <p className="text-sm font-black text-white">{worker.dob}</p>
              </div>
              {worker.designation && (
                <div className="bg-slate-800 px-4 py-2 rounded-xl border border-slate-700">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Designation</p>
                  <p className="text-sm font-black text-white">{worker.designation}</p>
                </div>
              )}
              <div className="flex items-center gap-3 bg-slate-800 px-5 py-2.5 rounded-xl border border-slate-700">
                <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all duration-700"
                    style={{ width: `${(uploadedCount / DOCUMENT_LIST.length) * 100}%` }}></div>
                </div>
                <span className="text-xs font-black text-white font-mono">{uploadedCount}/{DOCUMENT_LIST.length} Docs</span>
              </div>
              <button
                onClick={() => { setWorker(null); setUploadedDocs({}); setFormData({ formNo: '', name: '', mobileNo: '', panNo: '', aadhaarLast6: '' }); }}
                className="flex items-center gap-2 text-xs font-black uppercase text-slate-300 hover:text-white tracking-widest transition-all bg-slate-800 hover:bg-slate-700 border border-slate-700 px-4 py-2.5 rounded-xl"
              >
                <ArrowLeft size={14} /> Back
              </button>
            </div>
          </div>
        </div>

        {/* ── MAIN LAYOUT: LEFT CONTENT & RIGHT STATUS + CHAT PANEL ── */}
        <div className="max-w-[1700px] mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

            {/* ── LEFT & CENTER AREA (8 COLS): Profile & Documents ── */}
            <div className="lg:col-span-8 space-y-6">

              {/* Document Submission Hub */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
                  <div>
                    <h2 className="font-black text-slate-900 text-lg uppercase tracking-tight">Document Submission Hub</h2>
                    <p className="text-xs text-slate-500 font-semibold mt-0.5">Upload all 14 required documents in PDF/JPG format (max 200KB per file)</p>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider shrink-0 ${
                    requiredUploaded === totalRequired ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-orange-100 text-orange-800 border border-orange-300'
                  }`}>
                    {requiredUploaded}/{totalRequired} Mandatory Done
                  </div>
                </div>

                {/* Progress bar */}
                <div className="px-8 pt-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-black uppercase text-slate-500 tracking-wider">Overall Upload Progress</span>
                    <span className="text-xs font-mono font-black text-indigo-700">{uploadedCount} of {DOCUMENT_LIST.length} files uploaded</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full transition-all duration-700"
                      style={{ width: `${(uploadedCount / DOCUMENT_LIST.length) * 100}%` }}></div>
                  </div>
                </div>

                {/* Required Documents Grid */}
                <div className="px-8 pt-6 pb-4">
                  <p className="text-xs font-black uppercase text-slate-400 tracking-widest mb-4">Mandatory Documents ({totalRequired})</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {DOCUMENT_LIST.filter(d => d.required).map(doc => (
                      <DocUploadSlot
                        key={doc.key}
                        doc={doc}
                        uploaded={uploadedDocs[doc.key]}
                        formNo={worker.formNo}
                        workerName={worker.workerName}
                        designation={worker.designation}
                        onUploadSuccess={handleDocUploadSuccess}
                      />
                    ))}
                  </div>
                </div>

                {/* Optional Documents Grid */}
                <div className="px-8 pt-2 pb-8 border-t border-slate-100 mt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 my-4">
                    <p className="text-xs font-black uppercase text-slate-400 tracking-widest">Optional / Conditional Documents</p>
                    <span className="text-[10px] text-indigo-600 font-bold bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-200">
                      💡 Uploading an Additional Qualification reveals a new upload option automatically
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(() => {
                      const list = [
                        { key: 'eshramCard', label: 'e-Shram Card', format: 'PDF', required: false, note: 'Mandatory for Labour & Ancillary staff' }
                      ];

                      const addKeys = Object.keys(uploadedDocs).filter(k => k.startsWith('additionalQualification'));
                      let maxNum = 1;
                      addKeys.forEach(k => {
                        if (k !== 'additionalQualification') {
                          const match = k.match(/^additionalQualification_(\d+)$/);
                          if (match) {
                            const num = parseInt(match[1]);
                            if (num > maxNum) maxNum = num;
                          }
                        }
                      });

                      const lastKey = maxNum === 1 ? 'additionalQualification' : `additionalQualification_${maxNum}`;
                      const totalAddSlots = uploadedDocs[lastKey] ? maxNum + 1 : maxNum;

                      for (let i = 1; i <= totalAddSlots; i++) {
                        const key = i === 1 ? 'additionalQualification' : `additionalQualification_${i}`;
                        list.push({
                          key,
                          label: i === 1 ? 'Additional Qualification (if any)' : `Additional Qualification Certificate ${i}`,
                          format: 'PDF',
                          required: false,
                          note: i === 1 ? 'Optional' : 'Optional Multi-Upload'
                        });
                      }

                      return list.map(doc => (
                        <DocUploadSlot
                          key={doc.key}
                          doc={doc}
                          uploaded={uploadedDocs[doc.key]}
                          formNo={worker.formNo}
                          workerName={worker.workerName}
                          designation={worker.designation}
                          onUploadSuccess={handleDocUploadSuccess}
                          onDeleteDoc={handleDeleteDoc}
                        />
                      ));
                    })()}
                  </div>
                </div>
              </div>

              {/* Review & Submit Action Bar */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-3 h-3 rounded-full ${isFinalSubmitted ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`}></span>
                    <h3 className="font-black text-slate-900 text-sm uppercase tracking-wider">
                      {isFinalSubmitted ? '✅ Application Submitted to Admin' : '📋 Review Uploaded Documents'}
                    </h3>
                  </div>
                  <p className="text-xs text-slate-500 font-semibold">
                    {isFinalSubmitted
                      ? 'Your application & documents have been transmitted to SGCL Admin for verification.'
                      : 'Review all files, accept Terms & Conditions, and submit application to Admin.'
                    }
                  </p>
                </div>
                <button
                  onClick={() => setShowReviewModal(true)}
                  className={`px-6 py-3.5 rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-md active:scale-95 shrink-0 ${
                    isFinalSubmitted
                      ? 'bg-slate-900 text-white hover:bg-slate-800'
                      : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:brightness-110'
                  }`}
                >
                  {isFinalSubmitted ? '🔍 Review / Replace Documents' : '🔍 Review & Submit Application'}
                </button>
              </div>

              {/* Instructions Box */}
              <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6">
                <p className="text-xs font-black text-amber-900 uppercase tracking-widest mb-3">📌 Important Upload Instructions</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-amber-800 font-semibold leading-relaxed">
                  <p>• Scanned copies must be clear and legible</p>
                  <p>• PDF file size maximum 200KB per document</p>
                  <p>• Passport photo must be in JPG format with Blue background</p>
                  <p>• You can replace or re-upload documents anytime during review</p>
                </div>
              </div>
            </div>

            {/* ── RIGHT COLUMN (4 COLS): Status Box & Admin Remarks Box ── */}
            <div className="lg:col-span-4 space-y-6">

              {/* 1. Official Status Box (Stats Only) */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <h3 className="font-black text-slate-900 text-sm uppercase tracking-tight">SGCL Status Box</h3>
                  <span className="text-[10px] font-black text-green-600 bg-green-50 px-2.5 py-1 rounded-full uppercase border border-green-200">Live Status</span>
                </div>

                {/* Progress stats */}
                <div className="grid grid-cols-2 gap-4 pt-1">
                  <div className="bg-slate-50 rounded-2xl p-5 text-center border border-slate-200">
                    <p className="text-3xl font-black text-slate-800 font-mono">{uploadedCount}</p>
                    <p className="text-xs font-black text-slate-500 uppercase tracking-wider mt-1">Uploaded</p>
                  </div>
                  <div className="bg-orange-50 rounded-2xl p-5 text-center border border-orange-200">
                    <p className="text-3xl font-black text-orange-700 font-mono">{DOCUMENT_LIST.length - uploadedCount}</p>
                    <p className="text-xs font-black text-orange-600 uppercase tracking-wider mt-1">Pending</p>
                  </div>
                </div>
              </div>

              {/* 2. Admin Remarks & Action Notes Box (EXPANDED & PROMINENT) */}
              <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-8 border border-indigo-700/50 shadow-2xl text-white space-y-6">
                <div className="flex items-center justify-between border-b border-indigo-800/60 pb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🏛️</span>
                    <div>
                      <h3 className="font-black text-white text-sm uppercase tracking-wider">Admin Remarks & Action Notes</h3>
                      <p className="text-[10px] text-indigo-300 font-semibold">Official updates transmitted from SGCL Admin Panel</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-black text-amber-400 bg-amber-950/80 px-3 py-1 rounded-full uppercase border border-amber-700 shrink-0">Official Status</span>
                </div>

                <div className="bg-slate-800/90 rounded-2xl p-6 border border-slate-700/80 space-y-3 shadow-inner">
                  <p className="text-xs text-indigo-400 font-black uppercase tracking-widest flex items-center gap-2">
                    <span>📌 Current Admin Status Message:</span>
                  </p>
                  <p className="text-base sm:text-lg font-bold text-amber-300 leading-relaxed">
                    {worker.adminStatusMessage || 'Your application is registered. Pending document submission and admin review.'}
                  </p>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-400 font-semibold pt-2 border-t border-slate-800/60">
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span> Authorized: SGCL NFR Zone</span>
                  <span className="font-mono text-indigo-300 font-bold">{isFinalSubmitted ? 'Status: Transmitted' : 'Status: Draft Uploads'}</span>
                </div>
              </div>

              <p className="text-center text-slate-400 text-[10px] font-bold uppercase tracking-[0.3em] pt-4">
                Shramjivi Goodshed Corporation Limited · NFR Zone
              </p>

            </div>

          </div>
        </div>

        {/* ── MODAL: DOCUMENT REVIEW & TERMS AGREEMENT ── */}
        {showReviewModal && (
          <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden border border-slate-200 flex flex-col max-h-[90vh]">
              <div className="bg-slate-900 text-white px-8 py-5 flex justify-between items-center border-b-4 border-orange-500 shrink-0">
                <div>
                  <h3 className="font-black uppercase tracking-wider text-base">🔍 Document Review & Application Submission</h3>
                  <p className="text-xs text-slate-400 font-semibold">Verify your uploaded documents and accept Terms & Conditions to submit to Admin</p>
                </div>
                <button onClick={() => setShowReviewModal(false)} className="text-white hover:text-orange-400 text-2xl font-bold">✕</button>
              </div>

              <div className="p-8 overflow-y-auto space-y-6">
                <div className="bg-indigo-50 rounded-2xl p-4 border border-indigo-200 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black text-indigo-900 uppercase">Submission Progress</p>
                    <p className="text-xs text-indigo-700 font-semibold">{uploadedCount} of {DOCUMENT_LIST.length} files attached</p>
                  </div>
                  <span className="text-sm font-black font-mono text-indigo-700">{((uploadedCount / DOCUMENT_LIST.length) * 100).toFixed(0)}% Complete</span>
                </div>

                {/* Uploaded Documents List */}
                <div className="space-y-3">
                  <p className="text-xs font-black uppercase text-slate-500 tracking-wider">Uploaded Documents Summary</p>
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden">
                    {DOCUMENT_LIST.map(doc => {
                      const uploaded = uploadedDocs[doc.key];
                      return (
                        <div key={doc.key} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white hover:bg-slate-50">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${uploaded ? 'bg-green-500' : 'bg-slate-300'}`}></div>
                            <div>
                              <p className="text-xs font-black text-slate-800 uppercase">{doc.label}</p>
                              <p className="text-[10px] text-slate-400 font-semibold">
                                {uploaded ? `Uploaded ${new Date(uploaded.uploadedAt).toLocaleDateString('en-IN')}` : 'Not uploaded yet'}
                              </p>
                            </div>
                          </div>
                          {uploaded ? (
                            <div className="flex items-center gap-2">
                              <a
                                href={`${API_BASE}${uploaded.url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white rounded-lg text-[10px] font-black uppercase transition-all"
                              >
                                👁️ View File
                              </a>
                              <button
                                onClick={() => handleDeleteDoc(doc.key)}
                                className="px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-600 hover:text-white rounded-lg text-[10px] font-black uppercase transition-all"
                              >
                                🗑️ Remove
                              </button>
                            </div>
                          ) : (
                            <span className="text-[10px] font-black text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full uppercase border border-amber-200">Pending</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-3">
                  <p className="text-xs font-black text-slate-900 uppercase tracking-wider">📜 Terms & Conditions Declaration</p>
                  <p className="text-xs text-slate-600 leading-relaxed font-semibold">
                    I hereby declare that all uploaded documents (scans, ID proofs, and certificates) are genuine, authentic, and accurate. I understand that submitting fraudulent documents will result in immediate disqualification and legal action under SGCL NFR Zone rules.
                  </p>
                  <label className="flex items-start gap-3 cursor-pointer pt-2">
                    <input
                      type="checkbox"
                      checked={tncAgreed}
                      onChange={(e) => setTncAgreed(e.target.checked)}
                      className="mt-0.5 w-5 h-5 accent-orange-500 rounded cursor-pointer shrink-0"
                    />
                    <span className="text-xs font-black text-slate-800 uppercase tracking-tight">
                      I agree to the Terms & Conditions and confirm my document submission.
                    </span>
                  </label>
                </div>
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-200 shrink-0 flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="px-6 py-3 rounded-xl border border-slate-300 text-slate-700 text-xs font-black uppercase tracking-wider hover:bg-slate-200 transition-all"
                >
                  Close & Edit Files
                </button>
                <button
                  onClick={handleFinalSubmit}
                  disabled={!tncAgreed || submittingFinal}
                  className="px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl text-xs font-black uppercase tracking-wider hover:brightness-110 active:scale-95 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingFinal ? 'Submitting Application...' : 'Submit all documents'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }


  // Verification form (portal active)
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 font-sans text-slate-900">
      <div className="max-w-3xl mx-auto mb-6">
        <button
          onClick={() => window.location.href = '/'}
          className="bg-white px-5 py-2.5 rounded-xl shadow-sm border border-slate-200 text-slate-600 font-black text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 hover:bg-slate-50 transition-all active:scale-95"
        >
          <ArrowLeft size={14} /> Back to Portal
        </button>
      </div>

      <div className="max-w-3xl mx-auto bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-100">
        <div className="bg-slate-900 p-10 text-center border-b-8 border-orange-500">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-900/40 border border-green-800 mb-4">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-[9px] font-black text-green-400 uppercase tracking-[0.3em]">
              {portalConfig.mode === 'selected' ? '🔒 Selected Candidates Only' : '🌐 Open Verification Active'}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-black text-white uppercase italic tracking-tighter leading-none">
            Worker Verification Hub
          </h2>
          <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.3em] mt-3 opacity-80">
            Real-Time Registration Tracking & Document Submission
          </p>
        </div>

        <div className="p-8 md:p-14">
          <form onSubmit={handleVerify} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 md:col-span-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2">Acknowledgment No.</label>
              <input
                required
                className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-indigo-600 font-black text-sm transition-all font-mono"
                placeholder="SGWC/NFR/000000"
                value={formData.formNo}
                onChange={(e) => setFormData({ ...formData, formNo: e.target.value.toUpperCase() })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2">Full Name</label>
              <input
                required
                className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-indigo-600 font-bold text-sm transition-all"
                placeholder="AS PER OFFICIAL RECORDS"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2">Registered Mobile No.</label>
              <input
                required
                maxLength={10}
                className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-indigo-600 font-bold text-sm transition-all"
                placeholder="10 DIGIT MOBILE NUMBER"
                value={formData.mobileNo}
                onChange={(e) => setFormData({ ...formData, mobileNo: e.target.value.replace(/\D/g, '') })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2">PAN Card Number</label>
              <input
                required
                maxLength={10}
                className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-indigo-600 font-bold text-sm transition-all uppercase font-mono"
                placeholder="ABCDE1234F"
                value={formData.panNo}
                onChange={(e) => setFormData({ ...formData, panNo: e.target.value.toUpperCase() })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2">Aadhaar Number (Last 6 Digits)</label>
              <input
                required
                maxLength={6}
                className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:border-indigo-600 font-bold text-sm transition-all font-mono"
                placeholder="LAST 6 DIGITS ONLY"
                value={formData.aadhaarLast6}
                onChange={(e) => setFormData({ ...formData, aadhaarLast6: e.target.value.replace(/\D/g, '') })}
              />
            </div>

            {error && (
              <div className="md:col-span-2 p-4 bg-red-50 text-red-700 rounded-2xl flex items-start gap-3 border border-red-100">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <p className="text-[11px] font-bold leading-relaxed">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="md:col-span-2 w-full bg-slate-900 text-white py-5 rounded-2xl font-black uppercase tracking-[0.3em] text-xs shadow-xl hover:bg-indigo-900 transition-all flex items-center justify-center gap-3 disabled:opacity-50 active:scale-[0.98]"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : '🛡️ Track My Application'}
            </button>
          </form>

          <div className="mt-8 p-5 bg-slate-50 rounded-2xl border border-slate-100">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">🔒 Security Notice</p>
            <p className="text-[10px] text-slate-500 font-semibold leading-relaxed">
              All five fields must match your registered records exactly. Your data is verified against the SGCL secure database. If you face any issues, contact the Guwahati Zonal Office.
            </p>
          </div>
        </div>
      </div>
      <p className="mt-8 text-center text-slate-400 text-[9px] font-bold uppercase tracking-[0.4em] opacity-60">Secure Portal · Shramjivi Corp NFR Zone</p>
    </div>
  );
};

export default WorkerStatus;